import {SchoolModel} from './school.model';

export class SchoolActivityModel {

  school: SchoolModel;
  totals =  {
    totalStudents: 0,
    activatedCount: 0,
    activationRate: 0,
    totalAnswers: 0,
    avgAnswersPerStudent: 0,
    correctRate: 0
  };
  byGrade: SchoolActivityGradeModel[] = [];

  static generate(json: any): SchoolActivityModel {

    const item = new SchoolActivityModel();
    item.school = SchoolModel.generate(json.school);
    item.totals = {
      ...json.totals
    };

    item.byGrade = SchoolActivityGradeModel.generateList(json.byGrade);

    return item;

  }

  static generateList(jsonList: Array<any>): Array<SchoolActivityModel> {

    const list = [];

    for (const json of jsonList) {

      const item = this.generate(json);
      list.push(item);

    }

    return list;

  }

}

export class SchoolActivityGradeModel {

  grade: number = null;
  totalStudents = 0;
  activatedCount = 0;
  activatedRate = 0;

  static generate(json: any): SchoolActivityGradeModel {

    const activityGrade = new SchoolActivityGradeModel();
    activityGrade.grade = json.grade;
    activityGrade.totalStudents = json.totalStudents;
    activityGrade.activatedCount = json.activatedCount;
    activityGrade.activatedRate = json.activatedRate;

    return activityGrade;

  }

  static generateList(jsonList: Array<any>): Array<SchoolActivityGradeModel> {

    const list = [];

    for (const json of jsonList) {
      const item = this.generate(json);
      list.push(item);
    }

    return list;

  }

}

/**
 * A dual purpose model which fetches the activation by grade.
 * A grade can also be passed and then it fetches activation by group.
 */
export class SchoolActivationModel {

  grade: number = null;
  classes: Array<SchoolActivationGroupModel> = [];

  static generate(json: any): SchoolActivationModel {

    const item = new SchoolActivationModel();

    item.grade = json.grade;
    item.classes = SchoolActivationGroupModel.generateList(json.classes);

    return item;

  }

}

/**
 * The activation by indivudal grade model
 */
export class SchoolActivationGroupModel {

  groupId: string;
  name: string;
  grade: number;
  totalStudents: number;
  activatedCount: number;
  activationRate: number;

  static generate(json: any): SchoolActivationGroupModel {

    const item = new SchoolActivationGroupModel();
    item.groupId = json.groupId;
    item.name = json.name;
    item.grade = json.grade;
    item.totalStudents = json.totalStudents;
    item.activatedCount = json.activatedCount;
    item.activationRate = json.activationRate;

    return item;

  }

  static generateList(jsonList: Array<any>): Array<SchoolActivationGroupModel> {

    const list = [];

    for (const json of jsonList) {
      list.push(this.generate(json));
    }

    return list;

  }

}

/**
 * GET /analytics/:schoolId/skills — summary mode (no grade query).
 * One row per grade: averages across that grade’s objective skill groups.
 */
export class SchoolSkillsGradeSummaryModel {

  grade: number = null;
  baselineMastery = 0;
  currentMastery = 0;
  delta = 0;

  static generate(json: any): SchoolSkillsGradeSummaryModel {

    const item = new SchoolSkillsGradeSummaryModel();
    item.grade = json.grade;
    item.baselineMastery = json.baselineMastery;
    item.currentMastery = json.currentMastery;
    item.delta = json.delta;

    return item;

  }

  static generateList(jsonList: Array<any>): Array<SchoolSkillsGradeSummaryModel> {

    const list = [];

    for (const json of jsonList) {
      list.push(this.generate(json));
    }

    return list;

  }

}

/**
 * Skill group row inside GET /analytics/:schoolId/skills detail mode.
 */
export class SchoolSkillsSkillGroupModel {

  skillGroupId: string;
  name: string;
  baselineMastery = 0;
  currentMastery = 0;
  delta: number | null = null;
  coveredByPlacement = false;

  static generate(json: any): SchoolSkillsSkillGroupModel {

    const item = new SchoolSkillsSkillGroupModel();
    item.skillGroupId = json.skillGroupId != null ? String(json.skillGroupId) : json.skillGroupId;
    item.name = json.name;
    item.baselineMastery = json.baselineMastery;
    item.currentMastery = json.currentMastery;
    item.delta = json.delta;
    item.coveredByPlacement = json.coveredByPlacement;

    return item;

  }

  static generateList(jsonList: Array<any>): Array<SchoolSkillsSkillGroupModel> {

    const list = [];

    for (const json of jsonList) {
      list.push(this.generate(json));
    }

    return list;

  }

}

/**
 * Category aggregate in GET /analytics/:schoolId/skills detail mode.
 */
export class SchoolSkillsCategoryModel {

  category: string;
  baselineMastery = 0;
  currentMastery = 0;
  delta: number | null = null;
  coveredByPlacement = false;
  skillGroups: SchoolSkillsSkillGroupModel[] = [];

  static generate(json: any): SchoolSkillsCategoryModel {

    const item = new SchoolSkillsCategoryModel();
    item.category = json.category;
    item.baselineMastery = json.baselineMastery;
    item.currentMastery = json.currentMastery;
    item.delta = json.delta;
    item.coveredByPlacement = json.coveredByPlacement;
    item.skillGroups = SchoolSkillsSkillGroupModel.generateList(json.skillGroups || []);

    return item;

  }

  static generateList(jsonList: Array<any>): Array<SchoolSkillsCategoryModel> {

    const list = [];

    for (const json of jsonList) {
      list.push(this.generate(json));
    }

    return list;

  }

}

/**
 * GET /analytics/:schoolId/skills
 * - Summary: { mode: 'summary', grades }
 * - Detail: { mode: 'detail', grade, categories }
 */
export class SchoolSkillsAnalyticsModel {

  mode: 'summary' | 'detail' = 'summary';
  grades: SchoolSkillsGradeSummaryModel[] = [];
  grade: number = null;
  categories: SchoolSkillsCategoryModel[] = [];

  static generate(json: any): SchoolSkillsAnalyticsModel {

    const item = new SchoolSkillsAnalyticsModel();
    item.mode = json.mode;

    if (json.mode === 'summary') {
      item.grades = SchoolSkillsGradeSummaryModel.generateList(json.grades || []);
    } else {
      item.grade = json.grade;
      item.categories = SchoolSkillsCategoryModel.generateList(json.categories || []);
    }

    return item;

  }

}


